import { useState } from "react";
import { GlassCard, Button, StatCard, StatusPill, SecretReveal } from "@/shared/ui";
import { useMe, useLogout, useOrders } from "@/shared/api/hooks";
import { useSettings } from "@/shared/api/settings";
import { useI18n } from "@/shared/i18n";
import { AuthPanel } from "@/features/auth/AuthPanel";
import { KeysCard } from "./KeysCard";
import { OrderRow } from "./OrderRow";
import "@/features/auth/auth.css";
import "./account.css";

function fmtTs(ts: string): string {
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleString();
}

/** Logged-in / dev-key header strip with logout or a login affordance. */
function SessionBar({ onLogin }: { onLogin: () => void }) {
  const { settings, isSession } = useSettings();
  const { t } = useI18n();
  const logout = useLogout();
  return (
    <GlassCard pad="sm" className="between">
      {isSession ? (
        <>
          <div>
            <StatusPill tone="ok" dot>
              {t("Signed in")}
            </StatusPill>
            <span style={{ marginLeft: 10, color: "var(--text-secondary)", fontSize: 13 }}>
              {settings.email || t("Account")}
            </span>
          </div>
          <Button variant="ghost" onClick={logout}>
            {t("Sign out")}
          </Button>
        </>
      ) : (
        <>
          <div>
            <StatusPill tone="pending" dot>
              {t("Developer mode")}
            </StatusPill>
            <span style={{ marginLeft: 10, color: "var(--text-secondary)", fontSize: 13 }}>
              {t("Calling with X-API-Key")}
            </span>
          </div>
          <Button variant="ghost" onClick={onLogin}>
            {t("Sign in / Sign up")}
          </Button>
        </>
      )}
    </GlassCard>
  );
}

export function AccountPage() {
  const { hasKey, isSession } = useSettings();
  const { t } = useI18n();
  const me = useMe();
  const orders = useOrders();
  const [showAuth, setShowAuth] = useState(false);
  const [signupKey, setSignupKey] = useState<string | null>(null);

  const free = me.data?.free_tokens ?? null;
  const paid = me.data?.paid_tokens ?? null;
  const total = free != null && paid != null ? free + paid : null;
  const dash = (v: number | null) => (!hasKey ? "—" : (v ?? "…"));

  // Not authenticated at all → the account hub IS the auth screen.
  if (!hasKey) {
    return (
      <>
        <div className="page__head">
          <h1 className="page__title">{t("Account")}</h1>
          <p className="page__sub">
            {t(
              "Sign in or sign up to manage credits and API keys — or paste an API key via ⚙ in the top-right corner.",
            )}
          </p>
        </div>
        <AuthPanel onSignedUp={setSignupKey} />
      </>
    );
  }

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">{t("Account")}</h1>
        <p className="page__sub">{t("Credits, usage, orders and API keys.")}</p>
      </div>

      <div style={{ marginBottom: "var(--s3)" }}>
        <SessionBar onLogin={() => setShowAuth((s) => !s)} />
      </div>

      {/* Dev-key users opting into a real session. */}
      {!isSession && showAuth && (
        <div style={{ marginBottom: "var(--s3)" }}>
          <AuthPanel onSignedUp={setSignupKey} />
        </div>
      )}

      {/* One-time api_key from a just-completed signup. */}
      {signupKey && (
        <div style={{ marginBottom: "var(--s3)" }}>
          <GlassCard title={t("Signed up · your API key")} action={
            <Button variant="ghost" onClick={() => setSignupKey(null)}>{t("Got it")}</Button>
          }>
            <SecretReveal
              value={signupKey}
              note={t("This is your account's default key for X-API-Key calls.")}
            />
          </GlassCard>
        </div>
      )}

      <div className="acc-stats">
        <GlassCard>
          <StatCard label={t("Total credits")} value={dash(total)} sub={t("free + paid words")} />
        </GlassCard>
        <GlassCard>
          <StatCard label={t("Free credits")} value={dash(free)} />
        </GlassCard>
        <GlassCard>
          <StatCard label={t("Paid credits")} value={dash(paid)} />
        </GlassCard>
      </div>

      <div className="acc-cols">
        <GlassCard
          title={t("Recent usage")}
          action={
            <Button variant="ghost" onClick={() => me.refetch()} loading={me.isFetching}>
              {t("Refresh")}
            </Button>
          }
        >
          {me.isError ? (
            <div className="acc-empty">{t("Load failed — refresh to retry.")}</div>
          ) : !me.data?.recent_usage?.length ? (
            <div className="acc-empty">{t("No usage yet.")}</div>
          ) : (
            <div className="acc-list">
              {me.data.recent_usage.map((u) => (
                <div className="acc-row" key={u.request_id}>
                  <div className="acc-row__main">
                    <span className="acc-row__title">
                      {u.kind === "detect" ? `${t("AI detection")} · ` : ""}
                      {u.input_chars != null
                        ? t("{n} chars", { n: u.input_chars })
                        : t("Rewrite")}
                      {u.rounds != null ? ` · ${t("{n} rounds", { n: u.rounds })}` : ""}
                    </span>
                    <span className="acc-row__meta">{fmtTs(u.ts)}</span>
                  </div>
                  <div className="acc-row__right">
                    <span className="acc-charge">-{u.tokens_charged}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </GlassCard>

        <KeysCard />
      </div>

      <div style={{ marginTop: "var(--s3)" }}>
        <GlassCard title={t("Recent orders")} action={<Button variant="ghost" onClick={() => orders.refetch()}>{t("Refresh")}</Button>}>
          {orders.isError ? (
            <div className="acc-empty">{t("Load failed — refresh to retry.")}</div>
          ) : orders.isPending ? (
            <div className="acc-empty">{t("Loading…")}</div>
          ) : !orders.data?.length ? (
            <div className="acc-empty">
              {t("No orders yet. Orders placed under \"Buy Credits\" appear here.")}
            </div>
          ) : (
            <div className="acc-list">
              {orders.data.slice(0, 50).map((o) => (
                <OrderRow key={o.out_trade_no} order={o} />
              ))}
            </div>
          )}
        </GlassCard>
      </div>
    </>
  );
}
